import React, { useState, useEffect } from 'react';
import { useFirebase } from '../context/FirebaseContext';
import { useDarkMode } from '../context/ThemeContext';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import GoogleIcon from '@mui/icons-material/Google';
import { styled } from '@mui/material/styles';
import { theme } from '../constants/theme';

const GoogleButton = styled(Button)(({ darkMode }) => ({
  height: 50,
  borderRadius: theme.borderRadius.md,
  backgroundColor: darkMode ? '#2d2d2d' : '#fff',
  color: darkMode ? '#e5e7eb' : '#3c4043',
  fontSize: 16,
  fontWeight: 500,
  textTransform: 'none',
  border: darkMode ? '2px solid #404040' : '2px solid #dadce0',
  padding: '12px 24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: darkMode ? '#383838' : '#f8f9fa',
    border: darkMode ? '2px solid #505050' : '2px solid #dadce0',
  },
  '&:disabled': {
    backgroundColor: darkMode ? '#2d2d2d' : '#fff',
    color: darkMode ? '#e5e7eb' : '#3c4043',
    opacity: 0.6,
  },
}));

const LoginModal = () => {
  const { currentUser, signInWithGoogle } = useFirebase();
  const { darkMode } = useDarkMode();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal is alleen open als er geen gebruiker is ingelogd
  const isOpen = !currentUser;

  // Reset loading en error state wanneer modal opent/sluit
  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setError('');
    }
  }, [isOpen]);

  const handleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      await signInWithGoogle();
      // Modal verdwijnt automatisch omdat currentUser verandert
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);

      if (error.code === 'auth/popup-closed-by-user') {
        setError('Login geannuleerd. Probeer het opnieuw.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup werd geblokkeerd door uw browser. Sta popups toe en probeer opnieuw.');
      } else {
        setError('Login mislukt: ' + error.message);
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      aria-labelledby="login-modal"
      disableEscapeKeyDown
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{
        backgroundColor: darkMode ? '#242424' : '#fff',
        borderRadius: `${theme.borderRadius.lg}px`,
        padding: 4,
        boxShadow: theme.shadows.md,
        maxWidth: '500px',
        width: '90%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        outline: 'none',
        transition: 'background-color 0.3s ease'
      }}>
        {/* Logo */}
        <img
          src={darkMode 
            ? `${process.env.PUBLIC_URL}/assets/DataBandits-deflogo-GGL-darkmode.png`
            : `${process.env.PUBLIC_URL}/assets/DataBandits-deflogo-GGL.png`
          }
          alt="DataBandits Logo"
          style={{ 
            height: '50px', 
            width: '200px', 
            objectFit: 'contain'
          }}
        />

        {/* LinkCoder Title */}
        <h1 style={{ 
          margin: 0, 
          fontSize: '32px',
          fontFamily: 'Helvetica Neue Light, Open Sans, Helvetica, Arial, sans-serif',
          fontWeight: 300,
          color: theme.colors.primary
        }}>
          LinkCoder
        </h1>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}

        {/* Google Login Button or Loading */}
        {!loading ? (
          <GoogleButton
            fullWidth
            variant="outlined"
            onClick={handleSignIn}
            startIcon={<GoogleIcon />}
            darkMode={darkMode}
          >
            Inloggen met Google
          </GoogleButton>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 1 }}>
            <CircularProgress size={24} sx={{ color: theme.colors.primary }} />
            <span style={{ color: darkMode ? '#e5e7eb' : theme.colors.text }}>Inloggen...</span>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default LoginModal;
